import React, { useState } from "react";
import { Button, Form, message, Input, Space } from 'antd';
import { Link, useNavigate } from 'react-router-dom'
import { LockOutlined, UserOutlined, MailOutlined, SafetyOutlined, GiftOutlined } from '@ant-design/icons';
import { sendEmailCode, emailRegister, validateInviteCode } from "../../API";
import { useTranslation } from 'react-i18next';

import '../login/login.scss'

const Register = () => {

    const navigate = useNavigate()
    const { t } = useTranslation();

    const [form] = Form.useForm();

    // 发送验证码 60s 倒计时
    const [countdown, setCountdown] = useState(0);
    const [sending, setSending] = useState(false);
    // 注册提交中（含邀请码校验）
    const [submitting, setSubmitting] = useState(false);

    // 邮箱格式校验
    const validateEmail = (_, value) => {
        if (!value) {
            return Promise.reject(new Error(t('register.emailRequired')));
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return Promise.reject(new Error(t('register.emailInvalid')));
        }
        return Promise.resolve();
    };

    // 密码校验：8-20位且必须同时包含字母和数字
    const validatePassword = (_, value) => {
        if (!value) {
            return Promise.reject(new Error(t('register.passwordRequired')));
        }
        if (value.length < 8 || value.length > 20) {
            return Promise.reject(new Error(t('register.passwordLength')));
        }
        if (!/[a-zA-Z]/.test(value)) {
            return Promise.reject(new Error(t('register.passwordMustContainLetter')));
        }
        if (!/\d/.test(value)) {
            return Promise.reject(new Error(t('register.passwordMustContainDigit')));
        }
        return Promise.resolve();
    };

    // 确认密码校验
    const validateConfirmPassword = ({ getFieldValue }) => ({
        validator(_, value) {
            if (!value) {
                return Promise.reject(new Error(t('register.confirmPasswordRequired')));
            }
            if (getFieldValue('password') !== value) {
                return Promise.reject(new Error(t('register.confirmPasswordNotMatch')));
            }
            return Promise.resolve();
        },
    });

    // 用户名校验：4-20位，仅字母/数字
    const validateUsername = (_, value) => {
        if (!value) {
            return Promise.reject(new Error(t('register.usernameRequired')));
        }
        if (value.length < 4 || value.length > 20) {
            return Promise.reject(new Error(t('register.usernameLength')));
        }
        if (!/^[a-zA-Z0-9]+$/.test(value)) {
            return Promise.reject(new Error(t('register.usernameOnlyAlphanumeric')));
        }
        return Promise.resolve();
    };

    // 发送邮箱验证码
    const handleSendCode = () => {
        form.validateFields(['email']).then(values => {
            setSending(true);
            sendEmailCode({ email: values.email, type: "REGISTER" }).then(res => {
                if (res?.data?.code === 200) {
                    message.success(t('register.codeSentSuccess'));
                    // 固定 60s 倒计时
                    setCountdown(60);
                    const timer = setInterval(() => {
                        setCountdown(prev => {
                            if (prev <= 1) {
                                clearInterval(timer);
                                return 0;
                            }
                            return prev - 1;
                        });
                    }, 1000);
                } else {
                    message.error(res?.data?.msg || t('register.codeSendFailed'));
                }
            }).catch(err => {
                console.log(err);
                message.error(t('register.codeSendFailed'));
            }).finally(() => {
                setSending(false);
            });
        }).catch(() => {
            // 邮箱校验不通过，不发请求
        });
    };

    // 注册提交：若填写了邀请码，先调校验接口，通过后再注册
    const onFinish = (values) => {
        setSubmitting(true);
        const doRegister = () => {
            emailRegister({
                username: values.username,
                email: values.email,
                verifyCode: values.verifyCode,
                password: values.password,
                inviteCode: values.inviteCode || undefined
            }).then(res => {
                if (res?.data?.code === 200) {
                    message.success(t('register.registerSuccess'));
                    // 清除可能的残留 token，跳转到登录页
                    localStorage.removeItem("token");
                    navigate('/login');
                } else {
                    message.error(res?.data?.msg || t('register.registerFailed'));
                }
            }).catch(err => {
                console.log(err);
                message.error(t('register.registerFailed'));
            }).finally(() => {
                setSubmitting(false);
            });
        };

        const inviteCode = (values.inviteCode || '').trim();
        // 邀请码为选填：为空直接注册
        if (!inviteCode) {
            doRegister();
            return;
        }
        // 填写了邀请码：先校验有效性
        validateInviteCode({ inviteCode }).then(res => {
            if (res?.data?.code === 200 && res?.data?.data?.valid) {
                doRegister();
            } else {
                setSubmitting(false);
                message.error(res?.data?.msg || t('register.inviteCodeInvalid'));
            }
        }).catch(err => {
            console.log(err);
            setSubmitting(false);
            message.error(t('register.inviteCodeInvalid'));
        });
    };

    const initialValues = {
        username: '',
        email: '',
        verifyCode: '',
        password: '',
        confirmPassword: '',
        inviteCode: '',
    };

    return (
        <div className="login">
            <div className="left">
                <div className="car">
                    <img src={process.env.PUBLIC_URL + '/logo/car.png'} alt="" />
                </div>
            </div>
            <div className="right">
                <div className="title">
                    <div className="logo">
                        <img src={process.env.PUBLIC_URL + '/logo/logo.jpg'} alt='logo' width="54px" height='41px' />
                        {t('login.platform')}
                    </div>
                </div>
                <div className="type">{t('register.title')}</div>
                <Form
                    name="register_form"
                    className="login-form register-form"
                    form={form}
                    initialValues={initialValues}
                    autoComplete="off"
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="username"
                        rules={[{ validator: validateUsername }]}
                    >
                        <Input
                            prefix={<React.Fragment><UserOutlined className="site-form-item-icon" />&nbsp;{t('register.username')}</React.Fragment>}
                            placeholder={t('register.usernamePlaceholder')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        rules={[{ validator: validateEmail }]}
                    >
                        <Input
                            prefix={<React.Fragment><MailOutlined className="site-form-item-icon" />&nbsp;{t('register.email')}</React.Fragment>}
                            placeholder={t('register.emailPlaceholder')}
                        />
                    </Form.Item>

                    {/* 验证码 + 发送按钮，flex 布局避免高度不一致 */}
                    <Form.Item
                        name="verifyCode"
                        rules={[
                            { required: true, message: t('register.codeRequired') },
                            { pattern: /^\d{6}$/, message: t('register.codeInvalid') }
                        ]}
                    >
                        <div className="code-row">
                            <Input
                                className="code-row-input"
                                prefix={<React.Fragment><SafetyOutlined className="site-form-item-icon" />&nbsp;{t('register.verifyCode')}</React.Fragment>}
                                placeholder={t('register.codePlaceholder')}
                            />
                            <Button
                                className="code-row-btn"
                                onClick={handleSendCode}
                                disabled={countdown > 0 || sending}
                            >
                                {countdown > 0 ? `${countdown}s` : t('register.sendCode')}
                            </Button>
                        </div>
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[{ validator: validatePassword }]}
                    >
                        <Input.Password
                            prefix={<React.Fragment><LockOutlined className="site-form-item-icon" />&nbsp;{t('register.password')}</React.Fragment>}
                            placeholder={t('register.passwordPlaceholder')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[validateConfirmPassword]}
                    >
                        <Input.Password
                            prefix={<React.Fragment><LockOutlined className="site-form-item-icon" />&nbsp;{t('register.confirmPassword')}</React.Fragment>}
                            placeholder={t('register.confirmPasswordPlaceholder')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="inviteCode"
                    >
                        <Input
                            prefix={<React.Fragment><GiftOutlined className="site-form-item-icon" />&nbsp;{t('register.inviteCode')}</React.Fragment>}
                            placeholder={t('register.inviteCodePlaceholder')}
                        />
                    </Form.Item>

                    <div className="register">
                        <Link to="/login">{t('register.goLogin')}</Link>
                    </div>

                    <Form.Item style={{ margin: "20px 0 15px 0" }}>
                        <Button style={{ width: "100%" }} type="primary" htmlType="submit" loading={submitting} className="login-form-button subButton">
                            {t('register.registerBtn')}
                        </Button >
                    </Form.Item>
                </Form>
            </div>
            <footer>©2025昌谷（宁波）  备案号：<a target="_blank" href="https://beian.miit.gov.cn/#/Integrated/index">浙ICP备2023036666号-1</a></footer>
        </div>
    )
}

export { Register }
